/**
 * Client Invoices Page
 * View and pay invoices
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  CreditCard,
  Calendar,
  RefreshCw,
} from 'lucide-react';

interface Invoice {
  id: number;
  invoiceNumber: string;
  status: 'draft' | 'unpaid' | 'paid' | 'cancelled' | 'refunded';
  total: number;
  balance: number;
  invoiceDate: string;
  dueDate: string;
  paidDate?: string;
  items: Array<{
    id: number;
    description: string;
    amount: number;
  }>;
}

const statusConfig = {
  paid: {
    label: 'Paid',
    icon: CheckCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
    borderColor: 'border-green-700',
  },
  unpaid: {
    label: 'Unpaid',
    icon: Clock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
    borderColor: 'border-yellow-700',
  },
  overdue: {
    label: 'Overdue',
    icon: AlertTriangle,
    color: 'text-red-400',
    bgColor: 'bg-red-900/20',
    borderColor: 'border-red-700',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
    borderColor: 'border-gray-700',
  },
  refunded: {
    label: 'Refunded',
    icon: XCircle,
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/20',
    borderColor: 'border-purple-700',
  },
  draft: {
    label: 'Draft',
    icon: FileText,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
    borderColor: 'border-gray-700',
  },
};

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/client/invoices', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data.invoices);
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (invoice: Invoice) => {
    if (invoice.status !== 'unpaid') return false;
    return new Date(invoice.dueDate) < new Date();
  };

  const getInvoiceStatus = (invoice: Invoice) => {
    if (isOverdue(invoice)) return 'overdue';
    return invoice.status;
  };

  const filteredInvoices = invoices.filter((invoice) => {
    if (filterStatus === 'all') return true;
    return getInvoiceStatus(invoice) === filterStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const totalUnpaid = invoices
    .filter((inv) => inv.status === 'unpaid')
    .reduce((sum, inv) => sum + inv.balance, 0);

  const totalOverdue = invoices
    .filter((inv) => isOverdue(inv))
    .reduce((sum, inv) => sum + inv.balance, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <FileText className="w-8 h-8" />
          My Invoices
        </h1>
        <p className="text-gray-400 mt-2">View and pay your invoices</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-orange-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Invoices</p>
              <p className="text-2xl font-bold text-white mt-1">{invoices.length}</p>
            </div>
            <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-orange-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-yellow-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Unpaid</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {formatPrice(totalUnpaid)}
              </p>
            </div>
            <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-red-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Overdue</p>
              <p className="text-2xl font-bold text-red-400 mt-1">
                {formatPrice(totalOverdue)}
              </p>
            </div>
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-green-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Paid This Year</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {
                  invoices.filter(
                    (inv) =>
                      inv.status === 'paid' &&
                      new Date(inv.paidDate || '').getFullYear() === new Date().getFullYear()
                  ).length
                }
              </p>
            </div>
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'unpaid', 'overdue', 'paid', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
              filterStatus === status
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-700/50'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading invoices...</span>
          </div>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Invoices Found</h3>
          <p className="text-slate-400">
            {filterStatus === 'all'
              ? "You don't have any invoices yet."
              : `You don't have any ${filterStatus} invoices.`}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">Invoice</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Date</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Due Date</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Amount</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredInvoices.map((invoice) => {
                  const status = getInvoiceStatus(invoice);
                  const statusInfo = statusConfig[status];
                  const StatusIcon = statusInfo.icon;

                  return (
                    <tr key={invoice.id} className="hover:bg-slate-700/30 transition-colors">
                      <td className="p-4">
                        <Link
                          to={`/client/invoices/${invoice.id}`}
                          className="text-orange-400 hover:text-orange-300 font-medium"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                      </td>
                      <td className="p-4 text-gray-300">
                        {formatDate(invoice.invoiceDate)}
                      </td>
                      <td className="p-4">
                        <span
                          className={`flex items-center gap-1 ${
                            isOverdue(invoice) ? 'text-red-400' : 'text-gray-300'
                          }`}
                        >
                          <Calendar className="w-4 h-4" />
                          {formatDate(invoice.dueDate)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-white font-medium">
                          {formatPrice(invoice.total)}
                        </span>
                        {invoice.balance > 0 && invoice.balance < invoice.total && (
                          <div className="text-xs text-gray-400 mt-1">
                            Balance: {formatPrice(invoice.balance)}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/client/invoices/${invoice.id}`}
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="View Invoice"
                          >
                            <FileText className="w-4 h-4" />
                          </Link>
                          <button
                            className="p-2 text-slate-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {invoice.status === 'unpaid' && (
                            <Link
                              to={`/client/invoices/${invoice.id}/pay`}
                              className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-1.5 text-sm font-medium"
                            >
                              <CreditCard className="w-4 h-4" />
                              Pay Now
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
